// src/components/ecommerce/ProductFilters.jsx
import React, { useState, useEffect } from 'react';
import { 
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Star,
  DollarSign,
  Package,
  Tag,
  Check,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Grid,
  List,
  Heart,
  Zap,
  Truck,
  Shield,
  Clock
} from 'lucide-react';

const PRICE_RANGES = [
  { label: 'Under R100', min: 0, max: 100 },
  { label: 'R100 - R500', min: 100, max: 500 },
  { label: 'R500 - R1000', min: 500, max: 1000 },
  { label: 'R1000 - R2000', min: 1000, max: 2000 },
  { label: 'R2000 - R5000', min: 2000, max: 5000 },
  { label: 'Over R5000', min: 5000, max: 99999 }
];

export const ProductFilters = ({ 
  products = [],
  categories = [],
  filters = {},
  priceRange = [0, 99999],
  onFiltersChange,
  onPriceRangeChange,
  onClose,
  styling = {},
  className = '' 
}) => {
  const [collapsedSections, setCollapsedSections] = useState({});
  const [localFilters, setLocalFilters] = useState(filters);
  const [searchTerm, setSearchTerm] = useState('');

  // Extract unique values from products
  const brands = [...new Set(products.map(p => p.brand).filter(Boolean))].sort();
  const tags = [...new Set(products.flatMap(p => p.tags || []))].sort();
  const colors = [...new Set(products.flatMap(p => p.variants?.filter(v => v.type === 'color').map(v => v.name) || []))].sort();
  const sizes = [...new Set(products.flatMap(p => p.variants?.filter(v => v.type === 'size').map(v => v.name) || []))].sort();

  const maxPrice = Math.max(...products.map(p => p.price || 0), 99999);
  const minPrice = Math.min(...products.map(p => p.price || 0), 0);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFilterChange = (filterType, value, checked = null) => {
    const newFilters = { ...localFilters };

    if (filterType === 'priceRange') {
      onPriceRangeChange?.(value);
    } else if (Array.isArray(newFilters[filterType])) {
      if (checked === null) {
        // Toggle behavior
        if (newFilters[filterType].includes(value)) {
          newFilters[filterType] = newFilters[filterType].filter(item => item !== value);
        } else {
          newFilters[filterType] = [...newFilters[filterType], value];
        }
      } else {
        // Explicit checked behavior
        if (checked) {
          if (!newFilters[filterType].includes(value)) {
            newFilters[filterType] = [...newFilters[filterType], value];
          }
        } else {
          newFilters[filterType] = newFilters[filterType].filter(item => item !== value);
        }
      }
    } else {
      newFilters[filterType] = checked !== null ? checked : value;
    }

    setLocalFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      inStock: false,
      onSale: false,
      featured: false,
      rating: 0,
      brands: [],
      tags: [],
      colors: [],
      sizes: []
    };
    setLocalFilters(clearedFilters);
    onFiltersChange?.(clearedFilters);
    onPriceRangeChange?.([minPrice, maxPrice]);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters.inStock) count++;
    if (localFilters.onSale) count++;
    if (localFilters.featured) count++;
    if (localFilters.rating > 0) count++;
    count += (localFilters.brands || []).length;
    count += (localFilters.tags || []).length;
    count += (localFilters.colors || []).length;
    count += (localFilters.sizes || []).length;
    if (priceRange[0] !== minPrice || priceRange[1] !== maxPrice) count++;
    return count;
  };

  const FilterSection = ({ title, children, defaultOpen = true }) => {
    const isCollapsed = collapsedSections[title] ?? !defaultOpen;
    
    return (
      <div className="border-b border-gray-200 last:border-b-0">
        <button
          onClick={() => toggleSection(title)}
          className="w-full flex items-center justify-between py-4 text-left hover:bg-gray-50 px-1"
        >
          <span className="font-medium text-gray-900">{title}</span>
          {isCollapsed ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          )}
        </button>
        {!isCollapsed && (
          <div className="pb-4 px-1">
            {children}
          </div>
        )}
      </div>
    );
  };

  const CheckboxItem = ({ label, checked, onChange, count = null }) => (
    <label className="flex items-center gap-3 py-2 cursor-pointer hover:bg-gray-50 px-2 rounded">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
      />
      <span className="flex-1 text-sm text-gray-700">{label}</span>
      {count !== null && (
        <span className="text-xs text-gray-500">({count})</span>
      )}
    </label>
  );

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Filters</h3>
          {getActiveFiltersCount() > 0 && (
            <span 
              className="px-2 py-1 text-xs font-medium text-white rounded-full"
              style={{ backgroundColor: styling.primaryColor }}
            >
              {getActiveFiltersCount()}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {getActiveFiltersCount() > 0 && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" />
              Clear
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded lg:hidden"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filter Content */}
      <div className="max-h-96 lg:max-h-none overflow-y-auto">
        {/* Quick Filters */}
        <FilterSection title="Quick Filters">
          <div className="space-y-1">
            <CheckboxItem
              label="In Stock"
              checked={localFilters.inStock || false}
              onChange={(checked) => handleFilterChange('inStock', checked)}
              count={products.filter(p => p.quantity > 0).length}
            />
            <CheckboxItem
              label="On Sale"
              checked={localFilters.onSale || false}
              onChange={(checked) => handleFilterChange('onSale', checked)}
              count={products.filter(p => p.salePrice && p.salePrice < p.price).length}
            />
            <CheckboxItem
              label="Featured"
              checked={localFilters.featured || false}
              onChange={(checked) => handleFilterChange('featured', checked)}
              count={products.filter(p => p.featured).length}
            />
          </div>
        </FilterSection>

        {/* Price Range */}
        <FilterSection title="Price Range">
          <div className="space-y-4">
            {/* Price Range Slider */}
            <div>
              <div className="flex items-center justify-between mb-2 text-sm text-gray-600">
                <span>R{priceRange[0]}</span>
                <span>R{priceRange[1]}</span>
              </div>
              <div className="relative">
                <input
                  type="range"
                  min={minPrice}
                  max={maxPrice}
                  value={priceRange[0]}
                  onChange={(e) => {
                    const newMin = parseInt(e.target.value);
                    if (newMin <= priceRange[1]) {
                      onPriceRangeChange?.([newMin, priceRange[1]]);
                    }
                  }}
                  className="absolute w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{ zIndex: 1 }}
                />
                <input
                  type="range"
                  min={minPrice}
                  max={maxPrice}
                  value={priceRange[1]}
                  onChange={(e) => {
                    const newMax = parseInt(e.target.value);
                    if (newMax >= priceRange[0]) {
                      onPriceRangeChange?.([priceRange[0], newMax]);
                    }
                  }}
                  className="absolute w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{ zIndex: 2 }}
                />
              </div>
            </div>

            {/* Preset Price Ranges */}
            <div className="space-y-1">
              {PRICE_RANGES.map((range, index) => {
                const count = products.filter(p => {
                  const price = p.salePrice || p.price || 0;
                  return price >= range.min && (range.max === 99999 ? true : price <= range.max);
                }).length;
                
                return (
                  <button
                    key={index}
                    onClick={() => onPriceRangeChange?.([range.min, range.max === 99999 ? maxPrice : range.max])}
                    className={`w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-50 ${
                      priceRange[0] === range.min && 
                      priceRange[1] === (range.max === 99999 ? maxPrice : range.max)
                        ? 'bg-blue-50 text-blue-600' 
                        : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{range.label}</span>
                      <span className="text-gray-500">({count})</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </FilterSection>

        {/* Rating Filter */}
        <FilterSection title="Customer Rating">
          <div className="space-y-2">
            {[4, 3, 2, 1].map(rating => {
              const count = products.filter(p => Math.floor(p.rating || 0) >= rating).length;
              return (
                <button
                  key={rating}
                  onClick={() => handleFilterChange('rating', 
                    localFilters.rating === rating ? 0 : rating
                  )}
                  className={`w-full flex items-center gap-2 py-2 px-2 rounded text-sm hover:bg-gray-50 ${
                    localFilters.rating === rating ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${
                          i < rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span>& up</span>
                  <span className="ml-auto text-gray-500">({count})</span>
                </button>
              );
            })}
          </div>
        </FilterSection>

        {/* Categories */}
        {categories.length > 0 && (
          <FilterSection title="Categories">
            <div className="space-y-1">
              {categories.map(category => {
                const count = products.filter(p => p.category === category.id).length;
                return (
                  <button
                    key={category.id}
                    className="w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-50 text-gray-700"
                  >
                    <div className="flex items-center justify-between">
                      <span>{category.name}</span>
                      <span className="text-gray-500">({count})</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </FilterSection>
        )}

        {/* Brands */}
        {brands.length > 0 && (
          <FilterSection title="Brands">
            <div className="space-y-1">
              {/* Search brands */}
              {brands.length > 5 && (
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search brands..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              
              {brands
                .filter(brand => brand.toLowerCase().includes(searchTerm.toLowerCase()))
                .map(brand => {
                  const count = products.filter(p => p.brand === brand).length;
                  return (
                    <CheckboxItem
                      key={brand}
                      label={brand}
                      checked={(localFilters.brands || []).includes(brand)}
                      onChange={(checked) => handleFilterChange('brands', brand, checked)}
                      count={count}
                    />
                  );
                })}
            </div>
          </FilterSection>
        )}

        {/* Colors */}
        {colors.length > 0 && (
          <FilterSection title="Colors">
            <div className="grid grid-cols-4 gap-2">
              {colors.map(color => {
                const isSelected = (localFilters.colors || []).includes(color);
                return (
                  <button
                    key={color}
                    onClick={() => handleFilterChange('colors', color)}
                    className={`p-2 border-2 rounded-lg text-xs text-center transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 text-blue-600'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {color}
                  </button>
                );
              })}
            </div>
          </FilterSection>
        )}

        {/* Sizes */}
        {sizes.length > 0 && (
          <FilterSection title="Sizes">
            <div className="grid grid-cols-4 gap-2">
              {sizes.map(size => {
                const isSelected = (localFilters.sizes || []).includes(size);
                return (
                  <button
                    key={size}
                    onClick={() => handleFilterChange('sizes', size)}
                    className={`p-2 border-2 rounded-lg text-xs text-center transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 text-blue-600'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </FilterSection>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <FilterSection title="Tags" defaultOpen={false}>
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => {
                const isSelected = (localFilters.tags || []).includes(tag);
                return (
                  <button
                    key={tag}
                    onClick={() => handleFilterChange('tags', tag)}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${
                      isSelected
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </FilterSection>
        )}

        {/* Special Features */}
        <FilterSection title="Features" defaultOpen={false}>
          <div className="space-y-1">
            <CheckboxItem
              label="Free Shipping"
              checked={false}
              onChange={() => {}}
              count={products.filter(p => (p.price || 0) >= 500).length}
            />
            <CheckboxItem
              label="Digital Product"
              checked={false}
              onChange={() => {}}
              count={products.filter(p => p.digital).length}
            />
            <CheckboxItem
              label="Has Variants"
              checked={false}
              onChange={() => {}}
              count={products.filter(p => p.variants && p.variants.length > 0).length}
            />
          </div>
        </FilterSection>
      </div>

      {/* Apply Filters Button (Mobile) */}
      {onClose && (
        <div className="p-4 border-t lg:hidden">
          <button
            onClick={onClose}
            className="w-full py-3 text-white rounded-lg font-medium"
            style={{ backgroundColor: styling.primaryColor }}
          >
            Show Results ({products.length})
          </button>
        </div>
      )}
    </div>
  );
};